/**
 * Workspace chrome in the header.
 *
 * A founder reaches every workspace, so they get a switcher and a link to the
 * overview. A member reaches exactly one — for them a switcher would be a
 * control with nothing behind it, which reads as "there is more here you cannot
 * see". They get the workspace name and nothing else.
 *
 * A native `<select>` on purpose: keyboard- and screen-reader-complete without a
 * line of ARIA, and it is a list of destinations, not a custom widget.
 */
import { Building2, List } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { useSession } from '@/features/auth/SessionContext'
import { useI18n } from '@/i18n'
import { Link, buildPath, navigate } from '@/lib/router'

export function WorkspaceSwitcher({ activeId }: { activeId: string | null }) {
  const { t } = useI18n()
  const { workspaces, isFounder } = useSession()

  if (workspaces.length === 0) return null

  const active = workspaces.find((w) => w.id === activeId) ?? null

  if (!isFounder && workspaces.length === 1) {
    return (
      <span
        data-testid="workspace-name"
        className="flex items-center gap-2 text-[13px] font-medium text-ink-2"
      >
        <Icon icon={Building2} className="text-muted" />
        {workspaces[0].name}
      </span>
    )
  }

  return (
    <div
      className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
      data-testid="workspace-switcher"
    >
      {/* `sr-only` below `sm`: the select keeps its accessible name, the phone
          header keeps 80 px. */}
      <label htmlFor="workspace-switcher" className="sr-only text-[12px] text-muted sm:not-sr-only">
        {t('workspace.switchLabel')}
      </label>
      {/* A `<select>` is as wide as its longest option name. A Büro with a long
          name therefore pushed the header past a 320 CSS px viewport (WCAG 2.2
          §1.4.10); `min-w-0` + `max-w-[240px]` lets it shrink and truncate
          instead — the full name stays in the open list. */}
      <select
        id="workspace-switcher"
        value={active?.id ?? ''}
        onChange={(event) => navigate(buildPath('workspace', { workspaceId: event.target.value }))}
        className="h-8 min-w-0 max-w-[240px] rounded-sm border border-border-strong bg-white px-2 text-[13px] text-ink"
      >
        {active ? null : <option value="">{t('workspace.switchNone')}</option>}
        {workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>
            {workspace.name}
          </option>
        ))}
      </select>
      {/* Icon-only on a phone, with the same accessible name: the destination
          stays reachable, the row does not wrap for it. */}
      <Link
        to="/workspace"
        className="inline-flex items-center gap-1.5 rounded-xs text-[12px] text-ink-2 underline underline-offset-2"
      >
        <Icon icon={List} className="sm:hidden" />
        <span className="max-sm:sr-only">{t('workspace.indexLink')}</span>
      </Link>
    </div>
  )
}
